import React, { useState, useEffect } from 'react'
import $t from '../../../../i18n'
import { useDispatch, useSelector } from 'react-redux'
import Actions from '../../../../actions'
import { capitalize } from 'lodash'
import Alert from '../../../common/Alert'
import Page from '../../../common/Page/Page'
import NotInsuredLabel from '../../../common/NotInsuredLabel'
import { clinicTypes } from '../../../../consts'
import InviteToAppLabelAction from '../../../common/InviteToAppLabelAction'
import { isTaskCompleted } from '../../../../utils/onBoardingUtils'
import { DOWNLOAD_APP } from '../../../../consts/OnBoardingConsts'
import { getIsHmoMember } from '../../../../utils/hmoUtils'
import { isMemberMinor } from '../../../../utils/familyUtils'

import './FamilySelectPage.css'

export const FamilySelectPage = () => {
  const dispatch = useDispatch()
  const [memberSelected, setMemberSelected] = useState(null)
  const [isAlertVisible, setIsAlertVisible] = useState(false)

  const selectedService = useSelector(state => state.getCare.selectedService)
  const userDetails = useSelector(state => state.profile.userDetails)
  const { familyList, isFamilyListLoading } = useSelector(
    state => state.getCare
  )

  const members = useSelector(state => state.account.members)

  // some clinics are allowed only for the owner of the account
  const canSelectOnlyMyself = selectedService !== clinicTypes.PRIMARY

  // showing members without permissions as disabled
  const disabledMembers = members
    ?.filter(
      member =>
        !familyList.find(
          activemember =>
            activemember?.id?.toString() === member?.id?.toString()
        ) &&
        getIsHmoMember(member) &&
        !isTaskCompleted(member?.hmo_data?.on_boarding_tasks, DOWNLOAD_APP.name)
    )
    ?.map(member => ({ ...member, isDisabled: true }))

  const allMembers = familyList?.concat(disabledMembers)

  const myselfOption = {
    id: userDetails?.id,
    title: $t('familySelectPage.myself'),
    selected: memberSelected?.id === userDetails?.id,
    onClick: () => onMemberSelected(userDetails),
  }

  const familyMembers = allMembers?.reduce(
    (acc, member) => {
      acc.push({
        id: member?.id,
        title: `${capitalize(member.firstName)} ${capitalize(member.lastName)}`,
        selected: memberSelected?.id === member?.id,
        disabled: canSelectOnlyMyself || member?.isDisabled,
        shouldShowInviteOption: !!member?.isDisabled && !isMemberMinor(member),
        onClick: () => onMemberSelected(member),
      })

      return acc
    },
    [myselfOption]
  )

  useEffect(() => {
    dispatch(Actions.createAction(Actions.SHOW_FAMILY_MEMBERS_REQUESTED))
  }, [])

  const onMemberSelected = member => {
    setMemberSelected(member)
    if (member?.id !== userDetails?.id && member?.relation !== 'child') {
      setIsAlertVisible(true)
    } else {
      approveMemberSelection(member)
    }
  }

  const approveMemberSelection = member => {
    dispatch(
      Actions.createAction(Actions.CHOOSE_FAMILY_MEMBER, {
        member,
      })
    )

    setIsAlertVisible(false)

    // note - don't add 'onNext' to this screen because we will move to next screen automaticlly when member is selected (inside the wizard)
    // the on next is unnecessary in this page because when selecting a family member, we are already moving to the next page
  }

  return (
    <Page
      className="family-select-page"
      title={$t('familySelectPage.title')}
      loading={isFamilyListLoading}
      subtitle={
        canSelectOnlyMyself
          ? $t('homeScreen.getCare.alertClinicsForOthers.title')
          : null
      }
    >
      <div className="family-members-content">
        <ul className="family-members-list">
          {familyMembers?.map(member => (
            <li
              key={member?.id}
              className="member-item list-item"
              data-selected={member?.selected}
              data-disabled={member?.disabled}
              onClick={member?.disabled ? null : member?.onClick}
            >
              <span className="member-title">{member?.title}</span>
              {member?.shouldShowInviteOption && (
                <InviteToAppLabelAction patientId={member?.id} />
              )}
              <NotInsuredLabel patientId={member?.id} />
            </li>
          ))}
        </ul>
        <Alert
          title={$t('familySelectPage.adultConcent', {
            name: memberSelected?.firstName,
          })}
          shouldDisplay={isAlertVisible}
          onCancel={() => {
            setMemberSelected(null)
            setIsAlertVisible(false)
          }}
          onOk={() => approveMemberSelection(memberSelected)}
        />
      </div>
    </Page>
  )
}

export default FamilySelectPage
